import { escopoDoCache } from '@/lib/secretario/configuracaoAtual';

// Query key factories for consistent cache management
export const queryKeys = {
  plans: {
    public: ['plans', 'public'] as const,
    addons: ['plans', 'addons'] as const,
  },
  processos: {
    all: ['processos'] as const,
    detail: (id: string) => ['processos', id] as const,
    partes: (id: string) => ['processos', id, 'partes'] as const,
    dossie: (id: string) => ['processos', id, 'dossie'] as const,
    porVencimento: (de: string, ate: string) => ['processos', 'vencimento', de, ate] as const,
  },
  comunicacoes: {
    all: ['comunicacoes'] as const,
    detail: (id: string) => ['comunicacoes', id] as const,
    stats: ['comunicacoes', 'stats'] as const,
  },
  calendario: {
    eventos: ['calendario', 'eventos'] as const,
    agenda: ['calendario', 'agenda'] as const,
    googleStatus: ['calendario', 'google-status'] as const,
  },
  dashboard: {
    stats: ['dashboard', 'stats'] as const,
    regua: ['dashboard', 'regua'] as const,
  },
  modelos: {
    all: ['modelos'] as const,
  },
  // Um cache por Secretário (o pessoal e os dos escritórios): o segundo segmento é
  // a configuração escolhida na tela (docs/PLANO_SECRETARIO_ESCRITORIO.md §6). Por
  // isso getters — a chave é lida na hora, com a escolha de agora.
  secretario: {
    get config() {
      return ['secretario', escopoDoCache(), 'config'] as const;
    },
    acessiveis: ['secretario', 'acessiveis'] as const,
    modelos: (finalidade: string, advogadoId?: string | null) =>
      ['secretario', escopoDoCache(), 'modelos', finalidade, advogadoId ?? ''] as const,
    get modelosAll() {
      return ['secretario', escopoDoCache(), 'modelos'] as const;
    },
    get atendimentosAll() {
      return ['secretario', escopoDoCache(), 'atendimentos'] as const;
    },
    atendimentos: (fila: string, quemCuida = '') =>
      ['secretario', escopoDoCache(), 'atendimentos', fila, quemCuida] as const,
    atendimentosDoCliente: (clienteId: string) =>
      ['secretario', escopoDoCache(), 'atendimentos', 'cliente', clienteId] as const,
    atendimento: (id: string) => ['secretario', escopoDoCache(), 'atendimento', id] as const,
    contagens: (quemCuida = '') => ['secretario', escopoDoCache(), 'contagens', quemCuida] as const,
    get contagensAll() {
      return ['secretario', escopoDoCache(), 'contagens'] as const;
    },
    get numerosIgnorados() {
      return ['secretario', escopoDoCache(), 'numeros-ignorados'] as const;
    },
    get cota() {
      return ['secretario', escopoDoCache(), 'cota'] as const;
    },
  },
  timbrado: {
    me: ['timbrado', 'me'] as const,
    resolvido: ['timbrado', 'resolvido'] as const,
    /** O do escritório (a aba Peças dele). */
    escritorio: (id: string) => ['timbrado', 'escritorio', id] as const,
  },
  ia: {
    preferencias: ['ia', 'preferencias'] as const,
    prompts: ['ia', 'prompts'] as const,
    promptsSugeridos: ['ia', 'prompts', 'sugestoes'] as const,
    // Anexos da conversa. A chave carrega a CONVERSA porque a lista muda com
    // ela: os pendentes seguem o usuário, os adotados ficam na conversa que os
    // adotou. Fora da allowlist de persistência de propósito — o chat é
    // online-only, e um anexo em cache offline mentiria sobre o que a IA leu.
    anexos: (numero: string, sessaoId: string | null) => ['ia', 'anexos', numero, sessaoId ?? 'pendentes'] as const,
  },
  perfil: {
    me: ['auth', 'me'] as const,
  },
  documentos: {
    all: ['documentos'] as const,
    detail: (processoId: string) => ['documentos', processoId] as const,
    artefatos: ['artefatos'] as const,
    artefatoCounts: ['artefatos', 'counts'] as const,
  },
  push: {
    vapidKey: ['push', 'vapid-key'] as const,
    subscriptions: ['push', 'subscriptions'] as const,
  },
  admin: {
    logs: ['admin', 'logs'] as const,
    requests: ['admin', 'requests'] as const,
    userNames: ['admin', 'user-names'] as const,
    auditoria: ['admin', 'auditoria'] as const,
    changeHistory: ['admin', 'change-history'] as const,
    trackedTables: ['admin', 'tracked-tables'] as const,
    users: {
      all: ['admin', 'users'] as const,
      list: (params: Record<string, unknown>) =>
        ['admin', 'users', 'list', params] as const,
      detail: (id: string) => ['admin', 'users', 'detail', id] as const,
    },
  },
  monitoramento: {
    status: ['admin', 'monitoramento', 'status'] as const,
    serie: (alvo: string, janela: string) =>
      ['admin', 'monitoramento', 'serie', alvo, janela] as const,
    incidentes: (estado: string) => ['admin', 'monitoramento', 'incidentes', estado] as const,
    incidente: (id: number) => ['admin', 'monitoramento', 'incidente', id] as const,
    jobs: (tipo: string, status: string, page: number, pageSize: number) =>
      ['admin', 'monitoramento', 'jobs', tipo, status, page, pageSize] as const,
  },
  delegacoes: {
    dadas: ['delegacoes', 'dadas'] as const,
    recebidas: ['delegacoes', 'recebidas'] as const,
    resumo: ['delegacoes', 'recebidas', 'resumo'] as const,
    elegiveis: ['delegacoes', 'elegiveis'] as const,
    historico: (id: string) => ['delegacoes', id, 'historico'] as const,
  },
  escritorios: {
    all: ['escritorios'] as const,
    pendentes: ['escritorios', 'convites-pendentes'] as const,
    detail: (id: string) => ['escritorios', id] as const,
    convites: (id: string) => ['escritorios', id, 'convites'] as const,
    equipes: (id: string) => ['escritorios', id, 'equipes'] as const,
    compartilhamento: (id: string) => ['escritorios', id, 'compartilhamento'] as const,
    pedidos: (id: string) => ['escritorios', id, 'pedidos-remocao'] as const,
    /** Um item pessoal em cada escritório do dono (o "Compartilhar no escritório…"). */
    item: (tipo: string, id: string) => ['escritorios', 'item', tipo, id] as const,
    /** A fila "Para distribuir" do Gestor (§19). */
    fila: (id: string) => ['escritorios', id, 'distribuicao'] as const,
    /** Quem cuida de um processo num escritório (§19). */
    responsaveis: (id: string, processoId: string) => ['escritorios', id, 'responsaveis', processoId] as const,
    /** As regras de distribuição e o que elas distribuíram sozinhas (PLANO_REGRAS_DISTRIBUICAO). */
    regras: (id: string) => ['escritorios', id, 'regras-distribuicao'] as const,
    aplicadas: (id: string) => ['escritorios', id, 'regras-distribuicao', 'aplicadas'] as const,
    /** A aplicação das regras ao acervo existente (Bloco 3). */
    acervo: (id: string) => ['escritorios', id, 'regras-distribuicao', 'acervo'] as const,
    /** Os escritórios com processo esperando distribuição (aviso do painel do gestor). */
    distribuicaoPendente: ['escritorios', 'distribuicao-pendente'] as const,
  },
  compartilhamentos: {
    byRecurso: (tipo: string, id: string) => ['compartilhamentos', tipo, id] as const,
  },
  tribunais: {
    all: ['tribunais'] as const,
  },
  /** Tags e categorias visíveis (PLANO_TAGS): uma lista só, para o diálogo e o filtro. */
  tags: {
    all: ['tags'] as const,
  },
  anotacoes: {
    all: ['anotacoes'] as const,
    processo: (numero: string) => ['anotacoes', numero] as const,
  },
  // Editor dos autos: o hash da query já leva a conta em que se atua (delegação).
  editor: {
    rascunho: (numeroProcesso: string) => ['editor', 'rascunho', numeroProcesso] as const,
    versoes: (numeroProcesso: string) => ['editor', 'versoes', numeroProcesso] as const,
    versao: (numeroProcesso: string, id: string) => ['editor', 'versao', numeroProcesso, id] as const,
    estilos: ['editor', 'estilos'] as const,
    /** Os de um escritório pelo id (a aba Peças dele). */
    estilosDoEscritorio: (id: string) => ['editor', 'estilos', 'escritorio', id] as const,
  },
  processual: {
    all: ['processual'] as const,
    consulta: (numero: string) => ['processual', 'consulta', numero] as const,
    documento: (numero: string, uuid: string) =>
      ['processual', 'documento', numero, uuid] as const,
    familia: (numero: string) => ['processual', 'familia', numero] as const,
    capasRelacionados: (numeros: string[]) =>
      ['processual', 'capas-relacionados', [...numeros].sort().join(',')] as const,
    sincronizacao: (numero: string) => ['processual', 'sincronizacao-tribunal', numero] as const,
  },
  clientes: {
    all: ['clientes'] as const,
    detail: (id: string) => ['clientes', id] as const,
    counts: ['clientes', 'counts'] as const,
    search: ['clientes', 'search'] as const,
    processos: (id: string) => ['clientes', id, 'processos'] as const,
    artefatos: (id: string) => ['clientes', id, 'artefatos'] as const,
    peticoesIniciais: (id: string) => ['clientes', id, 'peticoes-iniciais'] as const,
  },
  peticoes: {
    all: ['peticoes'] as const,
    list: (filters?: Record<string, unknown>) =>
      ['peticoes', 'list', filters ?? {}] as const,
    detail: (id: string) => ['peticoes', 'detail', id] as const,
    recibo: (id: string) => ['peticoes', 'recibo', id] as const,
    counts: ['peticoes', 'counts'] as const,
  },
} as const;

// Per-endpoint timing configuration (from PLANO_OFFLINE_FIRST.md table 2.3)
const MINUTE = 60 * 1000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

export const queryTimings = {
  processos:     { staleTime: 5 * MINUTE, gcTime: 7 * DAY },
  comunicacoes:  { staleTime: 2 * MINUTE, gcTime: 7 * DAY },
  calendario:    { staleTime: 2 * MINUTE, gcTime: 30 * DAY },
  dashboard:     { staleTime: 1 * MINUTE, gcTime: 1 * DAY },
  modelos:       { staleTime: 30 * MINUTE, gcTime: 30 * DAY },
  perfil:        { staleTime: 10 * MINUTE, gcTime: 30 * DAY },
  timbrado:      { staleTime: 10 * MINUTE, gcTime: 30 * DAY },
  documentos:    { staleTime: 5 * MINUTE, gcTime: 30 * DAY },
  escritorios:   { staleTime: 5 * MINUTE, gcTime: 7 * DAY },
  processual:    { staleTime: 5 * MINUTE, gcTime: 7 * DAY },
  anotacoes:     { staleTime: 1 * MINUTE, gcTime: 7 * DAY },
  // O rascunho muda por outra pessoa (delegação) e o conflito o protege: fresco.
  editor:        { staleTime: 0.5 * MINUTE, gcTime: 7 * DAY },
} as const;

// Max age for the entire persisted cache (7 days)
export const PERSIST_MAX_AGE = 7 * DAY;

// Versão lógica do snapshot persistido. Ao mudar este valor, o React Query
// descarta o cache persistido anterior e começa um snapshot novo.
// v3: adicionado status SEM_DADOS no monitoramento (estende o vocabulário de
// StatusAlvo); cache antigo continuaria válido mas sem o novo valor.
// v4: shape de CacheInfo mudou (campos tipo_acesso/sistema_processual/age_seconds
// adicionados, source aceita só 'live'|'not_modified') e useProcessoJudicial
// passa a persistir { data, accepted, jobId, modo } em vez de só o snapshot.
// v5: useProcessoJudicial passa a persistir o campo `lastModified` junto com
// o snapshot (substitui o ref interno); sem isso o reload offline disparava
// fetch novo perdendo o round-trip 304 condicional.
// v6: Processo ganhou `assunto` (ProcessoOut/lista de processos persistida).
// v7: Parte ganhou `oculta`. Sem o bump, o 304 condicional reusava o snapshot
// antigo (sem o campo e com o cliente-lixo casado por nome na parte oculta).
// v8: Processo ganhou `favorito` ("acompanho de perto"). Sem o bump, a lista
// persistida antiga mostraria toda estrela apagada até o próximo refetch.
// v9: Processo ganhou `instancia` (filtro do Acervo). Sem o bump, a lista
// persistida antiga punha todo processo em "Não informada" até o refetch.
// v10: Processo, Cliente e PeticaoModelo ganharam `tags` e Artefato ganhou `etiquetas`
// (PLANO_TAGS). Sem o bump, a lista persistida antiga mostraria tudo sem tag.
// v11: Processo ganhou `vencimento` e `audiencia` (colunas do Acervo). Sem o bump, a
// lista persistida antiga mostraria as colunas vazias até o refetch.
// (A visão do escritório — `acesso`, `dono`, `advogados`, `recebida_por` — NÃO subiu o
// buster: os registros de colegas só vêm em chaves novas, e campo ausente é do próprio.)
// v13: o snapshot dos autos ganhou `_cache.certificado_de` (Bloco 3b): sem o bump, um
// 304 mantinha o snapshot lido antes com outro certificado, e a faixa de "dados
// públicos" aparecia sobre os autos completos do colega.
export const PERSIST_BUSTER = 'offline-cache-v13';

// Se o restore demorar demais, liberamos a UI e seguimos sem bloquear queries.
export const PERSIST_RESTORE_TIMEOUT_MS = 3 * 1000;
